import { createRouter, createWebHistory } from 'vue-router';
import HomePage from '@/views/HomePage.vue';
import CreateEvent from '@/views/CreateEvent.vue';
import CreateOrganiser from '@/views/CreateOrganiser.vue';
import MyTickets from '@/views/MyTickets.vue';
import LoginPage from '@/views/LoginPage.vue';
import RegisterPage from '@/views/RegisterPage.vue';
import EventDetails from '@/views/EventDetails.vue';
import TicketCheckout from '@/views/TicketCheckout.vue';
import Dashboard from '@/views/Dashboard.vue';
import OrganizerPage from '@/views/OrganizerPage.vue';
import EventList from '@/views/EventList.vue';
import EditEvent from '@/views/EditEvent.vue';
import AdminDashboard from '@/views/AdminDashboard.vue';
import PublicEvents from '@/views/PublicEvents.vue';

import store from '@/store';

const routes = [
  {
    path: '/',
    name: 'Home',
    component: HomePage
  },
  { 
    path: '/all-events', 
    name: 'PublicEvents',
    component: PublicEvents
  },
  { 
    path: '/events', 
    name: 'MyEvents',
    component: EventList, 
    meta: { requiresAuth: true } 
  },
  { 
    path: '/tickets', 
    name: 'MyTickets',
    component: MyTickets, 
    meta: { requiresAuth: true } 
  },
  { 
    path: '/events/create', 
    name: 'CreateEvent',
    component: CreateEvent, 
    meta: { requiresAuth: true } 
  },
  { 
    path: '/organiser/create', 
    name: 'CreateOrganiser',
    component: CreateOrganiser, 
    meta: { requiresAuth: true } 
  },
  { 
    path: '/login', 
    name: 'Login',
    component: LoginPage 
  },
  { 
    path: '/register', 
    name: 'Register',
    component: RegisterPage 
  },
  { 
    path: '/events/:id', 
    name: 'EventDetails',
    component: EventDetails
  },
  { 
    path: '/events/:id/edit', 
    name: 'EditEvent',
    component: EditEvent, 
    meta: { requiresAuth: true }
  },
  { 
    path: '/events/:id/tickets', 
    name: 'TicketCheckout',
    component: TicketCheckout,
    meta: { requiresAuth: true }
  },
  {
    path: '/events/:id/dashboard',
    name: 'Dashboard',
    component: Dashboard,
    meta: { requiresAuth: true }
  },
  {
    path: '/organizer/:organizerSlug',
    name: 'OrganizerPage',
    component: OrganizerPage,
    meta: {}
  },
  {
    path: '/organizer/profile/edit',
    name: 'EditOrganizerProfile',
    component: () => import('@/views/EditOrganizerProfile.vue'),
    meta: { requiresAuth: true, requiresOrganizer: true }
  },
  {
    path: '/admin',
    name: 'AdminDashboard',
    component: AdminDashboard,
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/qr-scanner',
    name: 'QRScanner',
    component: () => import('../views/QRScannerPage.vue'),
    meta: { requiresAuth: true }
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes
});

router.beforeEach((to, from, next) => {
  const user = store.state.user;
  const isOrganizerRole = !!(user && (user.isOrganizer || user.role === 'organizer' || user.role === 'admin'));

  // If user is logged in and tries to access home, send based on role
  if (user && to.path === '/') {
    const target = isOrganizerRole ? '/events' : '/tickets';
    if (to.path !== target) return next(target);
    return next();
  }

  if (to.meta.requiresAuth && !user) {
    return next('/login');
  }

  if (to.meta.requiresOrganizer && user && !isOrganizerRole) {
    return next('/tickets');
  }

  // Check admin access
  if (to.meta.requiresAdmin && user?.role !== 'admin') {
    return next('/');
  }
  
  // If user is logged in and trying to access login page, redirect based on role
  if (user && to.path === '/login') {
    const target = isOrganizerRole ? '/events' : '/tickets';
    if (to.path !== target) return next(target);
    return next();
  }

  // Buyers should not access /events pages
  if (user && to.path.startsWith('/events') && !isOrganizerRole) {
    if (to.path !== '/tickets') return next('/tickets');
    return next();
  }

  // Organizers/Admin should not stay on /tickets; send to /events
  if (user && to.path.startsWith('/tickets') && isOrganizerRole) {
    if (to.path !== '/events') return next('/events');
    return next();
  }
  
  next();
});

export default router;